import {html, LitElement, PropertyValues} from 'lit';
import {customElement, state} from "lit/decorators.js";
import {conf} from "../../config";

import {PlaceDetails, Map, Store, StoreOpening, WeekDay, DAYS_BY_INDEX} from "./models";
import favouriteStoreImg from "/src/assets/images/png/favouriteStore.png";
import {MarkerClusterer} from "@googlemaps/markerclusterer";

import styles from "./assets/scss/store-locator.scss?inline"
import {icons} from "./assets/icons/icons"

import {i18nService} from "../../services/i18nService";
import {storeService} from "../../services/store-service";

import {position} from "./utils/position";
import {visibilityController} from "./controller/visibility";
import {AccessibilityController} from "./controller/accessibility";
import {PlacesController} from "./controller/places";
import {MapController} from "./controller/map";
import {StoreController} from "./controller/store";
import {MarkerController} from "./controller/marker";
import {property} from 'lit/decorators.js';

@customElement('store-locator')
export class StoreLocator extends LitElement {
  @state() position?: google.maps.LatLng;
  @state() markers?: google.maps.marker.AdvancedMarkerElement[];
  @state() map: google.maps.Map | undefined;
  @state() resultList: (Store | PlaceDetails)[] = [];
  @state() stores: Store[] = [];
  @state() currentStore?: Store;
  @state() showInfoBox: boolean = false;
  @state() showLogin: boolean = false;
  @state() isMainContentInert: boolean = false;
  @state() showingResults: boolean = false;
  @state() favouriteId: string | undefined;
  @state() searchInput: string = "";
  @state() geolocationAvailable: boolean = false;
  @state() isTranslationsLoaded: boolean = false;

  //property zum steuern des input-focus, muss für die nutzung in specials (z.B. gluecksgefuehle) unterbunden werden, sonst springt/scrollt die seite direkt zum store-locator
  @property({ type: Boolean, attribute: 'disable-autofocus' })
  disableAutoFocus = false;

  private readonly countryCode: string;
  private debounceTimer?: number;
  private markerCluster?: MarkerClusterer;
  private centerControlCreated = false;

  private placesController = new PlacesController();
  private mapController = new MapController();
  private markerController = new MarkerController();
  private storeController = new StoreController(this.markerController);
  private accessibilityController!: AccessibilityController;

  constructor() {
    super()
    this.countryCode = storeService.getCountryCode()
  }

  // =========================
  // 🔹 LIFECYCLE
  // =========================

  async connectedCallback() {
    super.connectedCallback()
    this.stores = await storeService.loadStores()
    window.dispatchEvent(new CustomEvent('store-special'))
    await i18nService.initTranslations()
    this.isTranslationsLoaded = true;
    await this.loadGoogleMapsScript()
    await this.subscribeForMyStoreId()
    this.setStructuredData();
    // this.initLayoutState();
  }

  async disconnectedCallback() {
    super.disconnectedCallback()
  }

  updated(changedProps: PropertyValues) {
    if ((changedProps.has('isTranslationsLoaded') && this.isTranslationsLoaded) || (changedProps.has('map') && this.map)) {
      this.focusInputField();
      this.accessibilityController.initAccessibility(this.map);
    }
  }

  firstUpdated() {
    this.accessibilityController = new AccessibilityController(this.shadowRoot);
  }

  // =========================
  // 🔹 CSP-ADMIN SPECIAL API
  // =========================

  public setWhiteList(storesIds: string[]) {
    this.stores = this.stores.filter(store =>
      storesIds.includes(store.ax_id)
    );
  }

  // =========================
  // 🔹 AUTHENTICATION
  // =========================

  isLoggedIn() {
    return Boolean((window as any)?.NY_CUSTOMER_SERVICE?.currentCustomer);
  }

  // =========================
  // 🔹 GOOGLE MAPS SCRIPT & INITIALIZATION
  // =========================

  async loadGoogleMapsScript(): Promise<void> {
    if (!document.getElementById('google.maps.api')) {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `//maps.googleapis.com/maps/api/js?key=${conf.googleMapsKey}&libraries=geometry,visualization,marker&callback=initMap&loading=async`;
        script.id = 'google.maps.api';
        window.initMap = async () => {
          await this.initMap();
          resolve();
        };
        document.body.appendChild(script);
      });
    } else {
      await this.initMap()
      return Promise.resolve()
    }
  }

  async initMap() {
    this.position = await position.getPos(this.geolocationEnabled.bind(this), this.geolocationDisabled.bind(this))
    this.map = new google.maps.Map(this.shadowRoot?.getElementById('map') as HTMLElement, {
      mapId: conf.googleMapsId,
      mapTypeControl: false,
      streetViewControl: false,
      center: this.position,
      zoom: 7,
      fullscreenControl: false,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      zoomControl: true,
      keyboardShortcuts: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER,
      },
      scaleControl: true,
    })

    this.initMarkers()
    this.initMarkerCluster()
    this.initMarkerVisibilitySync()
  }

  // =========================
  // 🔹 GEOLOCATION HANDLERS
  // =========================

  geolocationEnabled(pos: google.maps.LatLng) {
    this.geolocationAvailable = true;

    const updatedMap = position.geolocationEnabled(pos, this.stores, this.map,
      {
        updateDistance: this.storeController.updateStoreDistance.bind(this.storeController),
        getClosest: this.storeController.getClosestStores.bind(this.storeController),
        createBounds: this.storeController.createBoundsForClosestStore.bind(this.storeController),
        createPositionMarker: this.markerController.createPositionMarker.bind(this.markerController),
      }
    );

    this.createCenterControl(updatedMap.pos);
    this.updateMap(updatedMap);
    return pos;
  }

  geolocationDisabled() {
    this.geolocationAvailable = false;
    const bounds = position.geolocationDisabled(this.stores, this.countryCode);
    this.mapController.fitBounds(this.map, bounds);
    return bounds.getCenter();
  }

  // =========================
  // 🔹 MAP UPDATES & CONTROLS
  // =========================

  updateMap(updatedMap: Map) {
    this.mapController.panToLocation(this.map, updatedMap.pos, 15);
    this.markers?.push(updatedMap.positionMarker);
    this.stores = updatedMap.updatedStores;
    this.resultList = updatedMap.closestStores;
    position.extendZoom(updatedMap);
  }

  createCenterControl(pos: google.maps.LatLng) {
    if (this.centerControlCreated) {
      return;
    }

    const icon = icons.renderMyLocation();

    this.mapController.createCenterControl(
      this.map,
      icon,
      () => this.mapController.panToLocation(this.map, pos, 15)
    );

    this.centerControlCreated = true;
  }

  // =========================
  // 🔹 MARKERS & CLUSTER MANAGEMENT
  // =========================

  initMarkers() {
    this.markers = this.markerController.createMarkers(
      this.map,
      this.stores,
      this.selectStore.bind(this)
    );
  }

  initMarkerCluster() {
    this.markerCluster = this.markerController.createMarkerCluster(this.markers, this.map)
  }

  initMarkerVisibilitySync() {
    visibilityController.syncMarkersOnBoundsChange(
      this.markerCluster,
      this.map,
      this.stores,
    );
  }

  // =========================
  // 🔹 FAVOURITE STORE & STORE SELECTION
  // =========================

  async subscribeForMyStoreId() {
    this.storeController.subscribeToFavouriteStore(this.stores, this.favouriteId,
      (id: string) => {
        this.favouriteId = id;
      },
      (store: Store | undefined) => {
        this.currentStore = store;

        if (store?.marker?.position) {
          this.mapController.panToLocation(this.map, store.marker.position, 18);
        }
      }
    );
  }

  setFavouriteStore(store?: Store) {
    this.storeController.setFavouriteStore(store, () => this.isLoggedIn(), this.favouriteId, this.stores, (id: string) => {
        this.favouriteId = id
      },
      () => {
        this.showLogin = true;
        this.isMainContentInert = true;
      }
    );
  }

  async selectStore(store: Store) {
    this.showInfoBox = true
    this.showingResults = false;
    this.markerController.handleMarkerChanged(store, this.currentStore);
    this.currentStore = store

    await this.updateComplete;
    const content = this.shadowRoot?.getElementById('close-dialog-btn') as HTMLElement;
    content?.focus();

    this.mapController.panToLocation(this.map, store.marker.position, 18);
  }

  // =========================
  // 🔹 GOOGLE PLACE HANDLING
  // =========================

  private async handleGooglePlaceSelection(place: PlaceDetails) {
    if (!place.placeId) {
      return;
    }

    this.showInfoBox = false;

    try {
      const details = await this.placesController.getPlaceDetails(place.placeId);

      if (!details.location) {
        return;
      }

      this.mapController.handlePlaceViewport(this.map, details.location, details.viewport);

      if (this.map) {
        this.resultList = this.storeController.getVisibleStoresRecursively(this.map, this.stores);
        await this.updateComplete;
      }

      this.searchInput = place.headline || details.displayName || '';
      this.shadowRoot?.querySelector<HTMLElement>('.result')?.focus();

    } catch (error) {
      console.error("failed to handle Google Place selection:", error);
    }
  }

  handleResultSelection(store: Store | PlaceDetails) {
    if (this.isGooglePlace(store)) {
      this.handleGooglePlaceSelection(store);
    } else {
      this.selectStore(store);
    }
  }

  private isGooglePlace(item: any): item is PlaceDetails {
    return (item as PlaceDetails).isGooglePlace;
  }

  // =========================
  // 🔹 SEARCH & INPUT HANDLING
  // =========================

  onInput = (e: Event) => {
    const el = e.target as HTMLInputElement;
    const input = el.value;

    this.searchInput = input;

    if (!input) {
      clearTimeout(this.debounceTimer);

      this.resultList = this.geolocationAvailable
        ? this.storeController.getClosestStores(3, this.stores)
        : [];

      this.showingResults = false;
      return;
    }

    if (input.length < 3) {
      clearTimeout(this.debounceTimer);
      this.resultList = [];
      return;
    }

    this.showingResults = true;
    this.showInfoBox = false;

    clearTimeout(this.debounceTimer);

    this.debounceTimer = window.setTimeout(async () => {
      const storeResults = this.storeController.filterStoresBySearchTerm(
        this.stores,
        input,
        this.favouriteId
      );

      const placesResults = await this.placesController.buildPlacesResults(input, this.countryCode, this.position);

      this.resultList = [...placesResults, ...storeResults];
    }, 400);
  };

  clearInputValue() {
    const element = this.shadowRoot?.getElementById('input') as HTMLInputElement
    element.value = ""
    this.resultList = this.geolocationAvailable
      ? this.storeController.getClosestStores(3, this.stores)
      : [];
    this.searchInput = ""

    element.focus();
  }

  async onSearchFocus() {
    this.showInfoBox = false
    this.showingResults = true;
  }

  // =========================
  // 🔹 JSON-LD & HELPER
  // =========================

  private isDesktopView(): boolean {
    return window.matchMedia("(min-width: 1025px)").matches;
  }

  async focusInputField() {
    if (this.disableAutoFocus) {
      return;
    }

    await this.updateComplete;
    (this.shadowRoot?.getElementById('input') as HTMLElement)?.focus();
  }

  private formatOpeningHours(store: Store): string[] {
    return store?.openings?.map((o: StoreOpening) => {
      const day = o?.week_day?.substring(0, 2).toUpperCase() ?? '??';
      const hours = o?.opening ? (o.closing ? `${o.opening}-${o.closing}` : o.opening) : 'Closed';

      return `${day} ${hours}`;
    }) ?? [];
  }

  setStructuredData(): void {
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": this.stores.map(store => ({
        "@type": "Store",
        "name": store.headline,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": [store.address_line_1, store.address_line_2, store.address_line_3].filter(Boolean).join(', '),
          "addressLocality": store.city,
          "postalCode": store.zip,
          "addressCountry": store.countryCode
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": store.lat,
          "longitude": store.lng,
        },
        "telephone": store.phone,
        "openingHours": this.formatOpeningHours(store)
      }))
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(jsonLd, null, 2);
    document.head.appendChild(script);
  }

  // =========================
  // 🔹 RENDER
  // =========================

  render() {
    if (!this.isTranslationsLoaded) {
      return html``;
    }

    return html`
        <style>${styles}</style>
        <div class="store-locator">
            <div class="overlay ${this.showLogin ? "active" : ""}"></div>
            ${this.renderSearchBar()}
            <div ?inert="${this.isMainContentInert}" class="content ${this.showInfoBox ? 'showing-info' : ''}">
                ${this.renderStoreInfo()}
            </div>
            <div ?inert="${this.isMainContentInert}" class="content ${this.showingResults ? 'showing-result' : ""}">
                ${this.renderResultList()}
            </div>
            <div id="map" class="map ${this.showInfoBox ? 'showing-info' : ''} ${this.showingResults ? 'showing-result' : ''}" role="region"
                 ?inert="${this.isMainContentInert}"
                 aria-label="${i18nService?.translate("store.map.description")}">
            </div>
            <login-dialog id="dialog"
                          class="login-dialog ${this.showLogin ? "not-logged" : ""}"
                          @close="${async () => {
                              this.showLogin = false;
                              this.isMainContentInert = false;
                              await this.updateComplete;
                              const likeBtn = this.shadowRoot?.querySelector('#like-btn') as HTMLElement;
                              likeBtn?.focus();
                          }}">
            </login-dialog>
        </div>
    `;
  }

  renderSearchBar() {
    const hasResults = this.searchInput.length;

    return html`
        <div class="search-bar ${this.showLogin ? 'hidden' : ''}">
            <div class="icon magnify" aria-hidden="true"
                 @click=${() => {
                     this.showingResults = false;
                     this.showInfoBox = false
                 }}>
                ${!this.isDesktopView() && this.showingResults ? icons.renderBackArrow() : icons.renderSearch()}
            </div>
            <div class="placeholder-container ${this.searchInput.length > 0 ? 'hidden' : ''}">
                <label for="input" aria-hidden="true">
                    ${i18nService?.translate("store.search.placeholder")}
                </label>
            </div>
            <input id="input"
                   .value="${this.searchInput}"
                   @click="${this.onSearchFocus}"
                   @keydown="${(e: KeyboardEvent) => this.accessibilityController.handleKeyDown(e, this.clearInputValue)}"
                   @input="${this.onInput}"
                   type="text"
                   role="combobox"
                   aria-autocomplete="list"
                   aria-expanded="${this.resultList.length > 0 ? 'true' : 'false'}"
                   aria-haspopup="listbox"
                   aria-label="${i18nService?.translate("store.search.options.subtitle")}"
                   autocomplete="off"
                   spellcheck="false">

            ${hasResults ? html`
                <button id="close-btn"
                        class="icon close btn"
                        @click=${this.clearInputValue}
                        aria-label="${i18nService?.translate("store.search.clear")}"
                        title="${i18nService?.translate("store.search.clear")}">
                    ${icons.renderClose()}
                </button>` : ""}
        </div>`
  }

  // =========================
  // 🔹 RESULT LIST RENDERING
  // =========================

  renderResultList() {
    const googlePlaces = this.resultList.filter(item => this.isGooglePlace(item));
    let storeResults = this.resultList.filter(item => !this.isGooglePlace(item));
    const favouriteStore = this.storeController.getStoreById(this.favouriteId, this.stores);

    if (favouriteStore) {
      storeResults = storeResults.filter(store => store.id !== this.favouriteId);
      storeResults = [favouriteStore, ...storeResults];
    }

    return html`
        <div class="result-list-container" role="listbox">
            ${googlePlaces.length > 0 ? html`
                <div class="google-places-section">
                    <div class="section-divider">${i18nService?.translate("store.google.results")}</div>
                    ${googlePlaces.map((place) => html`
                        <div class="google-result ${this.disableAutoFocus ? "whitelist" : ""}"
                             role="option"
                             tabindex="0"
                             aria-label="${place.headline}, ${place.address_line_1}, ${place.address_line_2}"
                             @click="${() => this.handleResultSelection(place)}"
                             @keydown="${(e: KeyboardEvent) => {
                                 if (e.key === 'Enter' || e.key === ' ') {
                                     e.preventDefault();
                                     this.handleResultSelection(place);
                                 }
                                 this.accessibilityController.handleKeyDown(e, this.clearInputValue);
                             }}">
                            <p>${place.headline.toLowerCase()}</p>
                        </div>
                    `)}
                </div>
            ` : ''}

            ${storeResults.length > 0 ? html`
                <div class="ny-stores-section">
                    <div class="section-divider">${i18nService?.translate("store.newyorker.results")}</div>
                    ${storeResults.map((store) => {
                        const isFav = store.id === this.favouriteId;
                        const distance = (store as Store).stringDistance;
                        const statusInfo = this.storeController.checkStoreOpenings(store as Store);
                        const todayClosing = this.storeController.getTodayClosingTime(store as Store);

                        return html`
                            <div class="ny-result ${this.disableAutoFocus ? "whitelist" : ""}"
                                 role="option"
                                 tabindex="0"
                                 aria-label="${store.headline}, ${store.address_line_1}, ${store.address_line_2}"
                                 @click="${() => this.handleResultSelection(store)}"
                                 @keydown="${(e: KeyboardEvent) => {
                                     if (e.key === 'Enter' || e.key === ' ') {
                                         e.preventDefault();
                                         this.handleResultSelection(store);
                                     }
                                     this.accessibilityController.handleKeyDown(e, this.clearInputValue);
                                 }}">
                                ${isFav ? html`
                                    <div class="isFavourite">
                                        ${icons.renderPNG(favouriteStoreImg)}
                                    </div>` : ""}
                                <p class="p1">${store.headline.toLowerCase()}</p>
                                <p>${store.address_line_1}${store.address_line_2 ? `, ${store.address_line_2}` : ""}</p>
                                <div class="store-meta">
                                    ${distance ? html`<p>${distance}</p>` : ''}
                                    ${distance && statusInfo ? html`<p class="separator">•</p>` : ''}
                                    ${html`${statusInfo}`}
                                    ${todayClosing ? html`<p class="separator">•</p>` : ''}
                                    ${html`<p>${todayClosing}</p>`}
                                </div>
                            </div>
                        `;
                    })}
                </div>
            ` : ''}
        </div>
    `;
  }

  // =========================
  // 🔹 STORE INFO RENDERING
  // =========================

  renderStoreInfo() {
    const store = this.currentStore;
    const navigationLink = `https://www.google.com/maps/dir/?api=1&destination=${store?.lat},${store?.lng}`;
    const id = this.favouriteId;
    const distance = (store as Store)?.stringDistance;
    const statusInfo = this.storeController.checkStoreOpenings(store as Store);
    const todayClosing = this.storeController.getTodayClosingTime(store as Store);

    return html`
        <section ?inert="${!this.showInfoBox}" class="info-box-container">
            <button class="close-btn close-icon"
                    aria-label="${i18nService?.translate("store.dialog.close.btn")}"
                    @click=${() => {
                        this.showInfoBox = false;
                        this.showingResults = false;
                        this.markerController.resetIcon(store)
                    }}>
                <span aria-hidden="true">${icons.renderCloseCircle()}</span>
            </button>
            <div class="store-address-wrapper">
                <button id="close-dialog-btn"
                        class="button-back"
                        aria-label="${i18nService?.translate("store.info.actions.back")}"
                        @click=${() => {
                            this.showInfoBox = false;
                            this.markerController.resetIcon(store);
                            this.focusInputField();
                        }}>
                    <span aria-hidden="true">${icons.renderBackArrow()}</span>
                </button>
                <div class="store-address"
                     title="${store?.headline}, ${store?.address_line_1}, ${store?.address_line_2}">
                    <p class="p1">${store?.headline.toLowerCase()}</p>
                    <p>${store?.address_line_1}, ${store?.address_line_2}</p>
                    <div class="store-meta">
                        ${distance ? html`<p>${distance}</p>` : ''}
                        ${distance && statusInfo ? html`<p class="separator">•</p>` : ''}
                        ${html`${statusInfo}`}
                        ${todayClosing ? html`<p class="separator">•</p>` : ''}
                        ${html`<p>${todayClosing}</p>`}
                    </div>
                </div>
            </div>
            <div class="store-actions" role="group"
                 aria-label="${i18nService?.translate("store.info.actions.group")}">
                <div>
                    <a href="${navigationLink}" target="_blank" class="tile navigation"
                       aria-label="${i18nService?.translate("store.info.actions.route")}">
                        <span aria-hidden="true">${icons.renderNavigation()}</span>
                    </a>
                    <p aria-hidden="true">Route</p>
                </div>
                <div>
                    <a href="tel:${store?.phone}" class="tile"
                       aria-label="${i18nService?.translate("store.info.actions.call")}">
                        <span aria-hidden="true">${icons.renderPhone()}</span>
                    </a>
                    <p aria-hidden="true">Call</p>
                </div>
                <div>
                    <button id="like-btn"
                            class="tile ${store?.id === id ? "favouriteButton" : ""}"
                            @click=${() => this.setFavouriteStore(store)}
                            aria-pressed="${store?.id === id}"
                            aria-label="${store?.id === id ? i18nService?.translate('store.favourite.remove') : i18nService?.translate('store.favourite.add')}">
                        <div class="heart" aria-hidden="true">
                            ${store?.id === id ? icons.renderPNG(favouriteStoreImg) : icons.renderHeartGrey()}
                        </div>
                    </button>
                    <p aria-hidden="true">Like</p>
                </div>
            </div>
            <div class="store-openings">
                <div class="info-box-openings">
                    <p class="title">${i18nService?.translate("store.openings")}</p>
                    ${this.renderOpeningTimes()}
                    <div class="opening-node">
                        <p>${store?.openingNote}</p>
                    </div>
                </div>
            </div>
            <div class="store-contact">
                <p class="title">${i18nService?.translate("store.phone")}</p>
                <p class="contact-line">${store?.phone}</p>
            </div>
        </section>`
  }

  // =========================
  // 🔹 OPENING HOURS RENDERING
  // =========================

  renderOpeningTimes() {
    const dayIndex = new Date().getDay();
    const currentDay = DAYS_BY_INDEX[dayIndex];

    const days: StoreOpening[] = []
    for (let weekDay in WeekDay) {
      const openingTime = this.currentStore?.openings.find(it => it.week_day.toLocaleLowerCase() === weekDay.toLocaleLowerCase())
      if (openingTime) {
        days.push(openingTime)
      } else {
        const closedDay = new StoreOpening({
          week_day: weekDay,
          opening: i18nService?.translate('store.closed'),
        })
        days.push(closedDay)
      }
    }

    return html`
        <dl class="openings-list" aria-label="${i18nService?.translate("store.openings")}">
            ${days.map((day: StoreOpening) => {
                const isToday = day.week_day.toUpperCase() === currentDay;

                return html`
                    <div class="openings ${isToday ? 'current-day' : ''}"
                         ?aria-current="${isToday ? 'date' : undefined}">
                        <dt class="day">${i18nService?.translate(day.week_day.toLocaleLowerCase())}</dt>
                        <dd class="times">
                            <div class="time">
                                ${day.opening}
                                ${day.closing ? html`<span aria-hidden="true"> - </span>${day.closing}` : ""}
                            </div>
                        </dd>
                    </div>`
            })}
        </dl>`
  }
}